import * as go from 'gojs';
import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { isDevelopment, isTesting } from 'vet-bones/utils';

import { INITIAL_LAYOUT_TRANSACTION_NAME } from 'src/containers/ProjectDiagram/constants/transactions';
import {
  hideContextMenu,
  showContextMenu,
} from 'src/containers/ProjectDiagram/handlers/contextMenu';
import { diagramChangedSelection } from 'src/containers/ProjectDiagram/handlers/diagram';
import {
  hideToolTip,
  showTooltip,
} from 'src/containers/ProjectDiagram/handlers/tooltip';
import { useDiagramObjectClick } from 'src/containers/ProjectDiagram/hooks/useDiagramObjectClick';
import { useDiagramObjectDoubleClick } from 'src/containers/ProjectDiagram/hooks/useDiagramObjectDoubleClick';
import { createDiagram } from 'src/containers/ProjectDiagram/templates/diagram';
import { ExtendedDiagram } from 'src/containers/ProjectDiagram/templates/extensions/diagram';
import { createModel } from 'src/containers/ProjectDiagram/templates/model';
import { ContextMenuData } from 'src/containers/ProjectDiagram/types/contextMenu';
import {
  ActiveDiagram,
  Diagram,
  DiagramLink,
  DiagramSnapshot,
  HeadlessDiagram,
} from 'src/containers/ProjectDiagram/types/diagram';
import { ModelData } from 'src/containers/ProjectDiagram/types/model';
import { TooltipData } from 'src/containers/ProjectDiagram/types/tooltip';
import { clearDiagramUndoHistory } from 'src/containers/ProjectDiagram/utils/clearDiagramUndoHistory';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
  commitDiagramWhileSkippingUndoManager,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { fixUnconnectedLinkPoints } from 'src/containers/ProjectDiagram/utils/fixUnconnectedLinkPoints';
import { rescaleDiagram } from 'src/containers/ProjectDiagram/utils/rescaleDiagram';
import { setDataProperty } from 'src/containers/ProjectDiagram/utils/setDataProperty';
import { setDiagramHighlight } from 'src/containers/ProjectDiagram/utils/setDiagramHighlight';
import { setDiagramSelection } from 'src/containers/ProjectDiagram/utils/setDiagramSelection';
import { shouldDispatchModelChange } from 'src/containers/ProjectDiagram/utils/shouldDispatchModelChange';
import { transformModelToSnapshot } from 'src/containers/ProjectDiagram/utils/transformModelToSnapshot';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

const isDev = isDevelopment();
const isTest = isTesting();

export const DiagramContext = React.createContext<ActiveDiagram | undefined>(
  undefined
);

/**
 * The active edit surface: a real `go.Diagram` (canvas built) or the headless
 * facade (large-model list view). Read `.model`/`.isReadOnly` directly on either
 * arm; narrow `!diagram.headless` before touching canvas-only members.
 */
export const useDiagram = () => {
  return React.useContext(DiagramContext);
};

type UseDiagramValueProps = {
  diagramSnapshot: DiagramSnapshot | null;
  handleDiagramUpdate: (snapshot: DiagramSnapshot) => void;
  handleDiagramSelection: (selection: go.Set<go.Part>) => void;
  headless: boolean;
  setContextMenu: (_: ContextMenuData | null) => void;
  setTooltip: (data: TooltipData | null) => void;
};

export const useDiagramValue = ({
  diagramSnapshot,
  handleDiagramUpdate,
  handleDiagramSelection,
  headless,
  setContextMenu,
  setTooltip,
}: UseDiagramValueProps) => {
  const { projectId } = useRouterParams();
  const processedIds = React.useRef<Set<string>>(new Set());
  const [activeValue, setActiveValue] = React.useState<
    ActiveDiagram | undefined
  >(undefined);

  const handleClick = useDiagramObjectClick();
  const handleDoubleClick = useDiagramObjectDoubleClick();

  let currentActive = activeValue;

  const diagramSnapshotId = diagramSnapshot?.id;
  const isNewSnapshot =
    !!diagramSnapshotId && !processedIds.current.has(diagramSnapshotId);
  // Rebuild when the view flips between diagram and headless — the mode is
  // decided upstream (from the model-size threshold), so a build here is the
  // single place that owns "diagram or headless model".
  const modeChanged =
    currentActive !== undefined && currentActive.headless !== headless;

  if (diagramSnapshotId && headless && (isNewSnapshot || modeChanged)) {
    processedIds.current.clear();
    processedIds.current.add(diagramSnapshotId);

    // A bare GraphLinksModel — no go.Diagram, no DOM — so the list view can
    // commit edits without paying for the visual tree. Mirrors changes back to
    // Redux with the same dispatch policy as the diagram listener below.
    const model = createModel(diagramSnapshot?.model);

    model.addChangedListener((e: go.ChangedEvent) => {
      if (shouldDispatchModelChange(e)) {
        const id = uuid();
        // Register before dispatching so the resulting snapshot doesn't rebuild
        // the model.
        processedIds.current.add(id);
        handleDiagramUpdate({ id, model: transformModelToSnapshot(e.model) });
      }
    });

    const headlessDiagram: HeadlessDiagram = {
      headless: true,
      isReadOnly: false,
      model,
    };

    currentActive = headlessDiagram;
    setActiveValue(headlessDiagram);
  } else if (diagramSnapshotId && !headless && (isNewSnapshot || modeChanged)) {
    processedIds.current.clear();
    processedIds.current.add(diagramSnapshotId);

    const diagram = createDiagram({
      modelSnapshot: diagramSnapshot?.model,
      showContextMenu: showContextMenu(
        document.createElement('div'),
        setContextMenu
      ),
      hideContextMenu: hideContextMenu(setContextMenu),
      showToolTip: showTooltip(setTooltip),
      hideToolTip: hideToolTip(setTooltip),
      diagramChangedSelection: diagramChangedSelection((e) =>
        handleDiagramSelection(e.diagram.selection)
      ),
      handleClick,
      handleDoubleClick,
    });

    let hasClearedInitialUndoHistory = false;

    diagram.addDiagramListener('InitialLayoutCompleted', (e) => {
      if (
        e.diagram.layout.isInitial ||
        e.diagram.model.modelData.flaggedForRedraw
      ) {
        setDiagramSelection()(e.diagram as ExtendedDiagram);
        const hasExistingViewport = e.diagram.model.modelData.viewportBounds;
        if (!hasExistingViewport) {
          requestAnimationFrame(() => {
            commitDiagramWhileSkippingUndoManager(
              diagram,
              (_, diagram) => rescaleDiagram(diagram),
              INITIAL_LAYOUT_TRANSACTION_NAME
            );
          });
        }

        commitDiagramModel(
          e.diagram as Diagram,
          (model, diagram) => {
            model.linkDataArray.forEach((linkData) => {
              fixUnconnectedLinkPoints(linkData)(model, diagram);
            });

            setDataProperty<ModelData>(model)(
              model.modelData,
              'flaggedForRedraw',
              false
            );
          },
          {
            skipsCheck: true,
            layout: DiagramModelCommitLayout.FULL,
            tname: INITIAL_LAYOUT_TRANSACTION_NAME,
          }
        );

        // Force the diagram to update and redraw after invalidating routes
        // This ensures links are actually rendered.
        requestAnimationFrame(() => {
          commitDiagramWhileSkippingUndoManager(
            e.diagram as Diagram,
            (_model, diagram) => {
              // Force all links to invalidate routes again within the transaction
              diagram.links.each((link) => {
                const diagramLink = link as DiagramLink;
                diagramLink.invalidateRoute();
              });
            }
          );

          e.diagram.updateAllTargetBindings();
        });

        if (e.diagram.layout.isInitial && !hasClearedInitialUndoHistory) {
          hasClearedInitialUndoHistory = true;
          // InitialLayoutCompleted fires while the "Initial Layout" transaction
          // is still open, so defer the clear a frame — clearing the undo
          // manager mid-transaction leaves GoJS unable to end it.
          requestAnimationFrame(() => {
            clearDiagramUndoHistory(e.diagram as Diagram, projectId);
          });
        }
      }
    });

    diagram.addModelChangedListener((e) => {
      if (
        e.model &&
        e.isTransactionFinished &&
        (e.propertyName === 'FinishedUndo' || e.propertyName === 'FinishedRedo')
      ) {
        diagram.commit(setDiagramHighlight());
        // undo/redo will restore diagram to previous viewstate while sometimes
        // ignoring current bindings (ex: fading with selection)
        diagram.updateAllTargetBindings('selection');
      }
      if (shouldDispatchModelChange(e)) {
        const id = uuid();
        processedIds.current.add(id);
        handleDiagramUpdate({ id, model: transformModelToSnapshot(e.model) });
      }
      if (
        e.model?.modelData.hiddenModelIds.length > 0 &&
        e.oldValue === 'Initial Layout'
      ) {
        diagram.updateAllTargetBindings('visible');
      }
    });

    if (isDev || isTest) {
      window.diagram = diagram;
    }

    currentActive = diagram;
    setActiveValue(diagram);
  }

  return currentActive;
};
